import Link from "next/link";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { UpgradePlanModal } from "../billing/UpgradePlanModal";
import {
  thumbMediaClassName,
  thumbMediaReadyClassName,
  thumbMediaWrapClassName,
  thumbSkeletonClassName,
} from "../editor/thumbMediaStyles";
import { insufficientReelCreditsEvent } from "./reel-creation-errors";

type CreatePageShellProps = {
  children: ReactNode;
  compact?: boolean;
  description: string;
  previewPosterUrl?: string;
  previewVideoUrl?: string;
  previewTitle: string;
  templateName: string;
};

export const createFormSectionClassName =
  "grid gap-3 rounded-[18px] border border-white/[0.055] bg-white/[0.018] p-4";
export const createFormSectionHeaderClassName =
  "flex items-center justify-between gap-2.5";
export const createFormSectionTitleClassName =
  "m-0 text-[0.95rem] font-extrabold";
export const createFieldLabelClassName = "grid gap-2";
export const createFieldLabelTextClassName =
  "text-[0.88rem] font-bold tracking-[-0.02em] text-[var(--rm-text)]";
/** Keeps paired controls together on larger screens and stacks them on narrow screens. */
export const createSplitFieldsClassName =
  "grid grid-cols-1 gap-3 min-[800px]:grid-cols-2";
export const createFooterClassName =
  "mt-2 flex items-end justify-between gap-[18px] rounded-[18px] border border-[rgba(181,140,248,0.14)] p-4 max-[720px]:flex-col max-[720px]:items-start";
export const createFooterBackground =
  "linear-gradient(90deg, rgba(138, 63, 252, 0.11), rgba(255, 255, 255, 0.018)), rgba(255, 255, 255, 0.018)";
export const createQuoteClassName = "grid gap-1.5";
export const createQuoteAmountClassName =
  "font-display text-[1.65rem] tracking-[-0.05em]";
export const createEyebrowClassName =
  "m-0 text-[0.78rem] font-bold uppercase tracking-[0.16em] text-[var(--rm-text-muted)]";
export const createSupportingTextClassName =
  "leading-[1.65] text-[var(--rm-text-soft)]";

const createPageClassName = "grid w-full min-w-0 gap-7";
const createLayoutClassName =
  "grid w-full min-w-0 grid-cols-1 gap-[18px] min-[1181px]:grid-cols-[minmax(0,1.42fr)_minmax(320px,0.78fr)]";
const createFormPanelClassName =
  "relative grid min-w-0 gap-4 overflow-hidden rounded-3xl border border-white/[0.08] p-[22px] shadow-[0_18px_54px_rgba(0,0,0,0.28)] max-[720px]:p-[22px]";
const createFormPanelBackground =
  "radial-gradient(circle at 88% 0%, rgba(138, 63, 252, 0.12), transparent 26%), linear-gradient(180deg, rgba(255, 255, 255, 0.035), rgba(255, 255, 255, 0.015)), rgba(12, 12, 14, 0.94)";
const createIntroClassName = "grid gap-2.5";
const createBackLinkClassName = "shrink-0 font-bold text-[var(--rm-text-soft)]";
const createIntroTitleClassName =
  "mt-3.5 font-display text-[clamp(1.85rem,3vw,2.55rem)] tracking-[-0.06em]";
const createIntroTemplateNameClassName =
  "inline-block bg-[linear-gradient(135deg,hsl(var(--primary))_0%,hsl(var(--accent))_100%)] bg-clip-text text-transparent [-webkit-text-fill-color:transparent]";
const createIntroDescriptionClassName =
  "mb-3.5 max-w-[62ch] text-[var(--rm-text-soft)] leading-relaxed";
const previewPanelClassName =
  "grid content-start gap-3 self-start rounded-3xl border border-white/[0.07] p-[18px] shadow-[0_18px_54px_rgba(0,0,0,0.26)] max-[720px]:p-[22px] max-[1180px]:static max-[1180px]:mx-auto max-[1180px]:w-full max-[1180px]:min-w-0 max-[1180px]:max-w-[560px] min-[1181px]:sticky min-[1181px]:top-[92px] min-[1181px]:min-w-0 min-[1181px]:max-h-[calc(100dvh-104px)] min-[1181px]:overflow-y-auto";
const previewPanelBackground =
  "radial-gradient(circle at top right, rgba(138, 63, 252, 0.16), transparent 28%), rgba(12, 12, 12, 0.95)";
const previewPillClassName =
  "inline-flex min-h-7 w-fit items-center gap-2 rounded-full border border-white/[0.08] px-[11px] text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-[var(--rm-text)]";
const previewPillDotClassName = "size-[7px] rounded-full bg-[#b58cf8]";
/** Full width inside the preview card; min-w-0 avoids grid/aspect-ratio overflow quirks. */
const phonePreviewVideoWidthClassName =
  "mx-auto w-[min(100%,340px,calc((100vh-190px)*0.5625))] min-w-0";
const phonePreviewClassName = cn(
  "relative aspect-[9/16] overflow-hidden rounded-[34px] border border-white/[0.08] p-5",
  phonePreviewVideoWidthClassName,
);
const phonePreviewBackground =
  "linear-gradient(180deg, rgba(18, 18, 18, 0.14), rgba(18, 18, 18, 0.72)), radial-gradient(circle at center, rgba(138, 63, 252, 0.1), transparent 36%), #060606";
const phonePreviewVideoOnlyClassName = cn(
  "relative aspect-[9/16] overflow-hidden rounded-3xl",
  phonePreviewVideoWidthClassName,
);
const phonePreviewVideoClassName = "absolute inset-0 size-full object-cover";
const phonePreviewGlowClassName =
  "absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.12),transparent_12%),linear-gradient(180deg,rgba(255,255,255,0.03),transparent_50%)]";
const phonePreviewPlayClassName = "absolute inset-0 grid place-items-center";
const phonePreviewPlayButtonClassName =
  "size-28 rounded-full bg-white/[0.96] [clip-path:polygon(38%_28%,74%_50%,38%_72%)]";
const phonePreviewCaptionClassName =
  "absolute bottom-[122px] left-6 right-6 text-center font-display text-[clamp(1.45rem,1.8vw,2rem)] font-extrabold italic tracking-[-0.06em] text-[#bf8cff]";
const phonePreviewTimelineClassName =
  "absolute bottom-[70px] left-7 right-7 h-1 rounded-full bg-white/20";
const phonePreviewTimelineProgressClassName =
  "block h-full w-[38%] rounded-[inherit] bg-[linear-gradient(90deg,#d7befc_0%,var(--rm-primary)_100%)]";
const phonePreviewControlsClassName =
  "absolute bottom-7 left-0 right-0 flex justify-center gap-4";
const phonePreviewControlDotClassName =
  "size-3.5 rounded-full border-2 border-white/[0.86]";
const phonePreviewControlPrimaryClassName =
  "size-[46px] rounded-full bg-white/[0.96]";

export function CreatePageShell({
  children,
  compact = false,
  description,
  previewPosterUrl,
  previewVideoUrl,
  previewTitle,
  templateName,
}: CreatePageShellProps) {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [previewState, setPreviewState] = useState<{
    phase: "loading" | "ready" | "error";
    url: string | undefined;
  }>({
    phase: previewVideoUrl ? "loading" : "error",
    url: previewVideoUrl,
  });
  const previewPhase =
    previewState.url === previewVideoUrl
      ? previewState.phase
      : previewVideoUrl
        ? "loading"
        : "error";

  useEffect(() => {
    const openUpgradeModal = () => setIsUpgradeModalOpen(true);
    window.addEventListener(insufficientReelCreditsEvent, openUpgradeModal);

    return () => {
      window.removeEventListener(insufficientReelCreditsEvent, openUpgradeModal);
    };
  }, []);

  const previewContent = (
    <>
      <div className={previewPillClassName}>
        <span className={previewPillDotClassName} />
        <span>Preview</span>
      </div>

      <div
        aria-hidden="true"
        className={
          previewVideoUrl
            ? phonePreviewVideoOnlyClassName
            : phonePreviewClassName
        }
        style={
          previewVideoUrl ? undefined : { background: phonePreviewBackground }
        }
      >
        {previewVideoUrl ? (
          <div
            aria-hidden
            className={cn(thumbMediaWrapClassName, "absolute inset-0")}
          >
            {previewPhase === "loading" && !previewPosterUrl ? (
              <div aria-hidden className={thumbSkeletonClassName} />
            ) : null}
            <video
              autoPlay
              className={cn(
                phonePreviewVideoClassName,
                thumbMediaClassName,
                previewPhase === "ready" && thumbMediaReadyClassName,
              )}
              loop
              muted
              onError={() => {
                setPreviewState({ phase: "error", url: previewVideoUrl });
              }}
              onLoadedData={() => {
                setPreviewState({ phase: "ready", url: previewVideoUrl });
              }}
              playsInline
              poster={previewPosterUrl}
              preload="metadata"
              src={previewVideoUrl}
            />
          </div>
        ) : (
          <>
            <div className={phonePreviewGlowClassName} />
            <div className={phonePreviewPlayClassName}>
              <div className={phonePreviewPlayButtonClassName} />
            </div>
          </>
        )}
        {!previewVideoUrl ? (
          <>
            <div className={phonePreviewCaptionClassName}>{previewTitle}</div>
            <div className={phonePreviewTimelineClassName}>
              <span className={phonePreviewTimelineProgressClassName} />
            </div>
            <div className={phonePreviewControlsClassName}>
              <span className={phonePreviewControlDotClassName} />
              <span className={phonePreviewControlPrimaryClassName} />
              <span className={phonePreviewControlDotClassName} />
            </div>
          </>
        ) : null}
      </div>
    </>
  );

  return (
    <>
      <div
        className={cn(
          createPageClassName,
          compact && "max-w-[1380px] gap-4 pt-8 pb-10",
        )}
      >
        <section className={createLayoutClassName}>
          <div
            className={cn(
              createFormPanelClassName,
              compact && "rounded-xl bg-[#14161a] p-4 shadow-none",
            )}
            style={compact ? undefined : { background: createFormPanelBackground }}
          >
            <div className={createIntroClassName}>
              <Link
                className={cn(
                  createBackLinkClassName,
                  compact && "text-[0.78rem] font-medium",
                )}
                href="/app/dashboard"
              >
                {"<"} Back
              </Link>
              <h2
                className={cn(
                  createIntroTitleClassName,
                  compact && "mt-1 text-[clamp(1.15rem,1.8vw,1.45rem)] font-semibold tracking-[-0.03em]",
                )}
              >
                {compact ? `New ${templateName}` : (
                  <>
                    <span>New Reel </span>
                    <span className={createIntroTemplateNameClassName}>
                      {templateName}
                    </span>
                  </>
                )}
              </h2>
              <p
                className={cn(
                  createIntroDescriptionClassName,
                  compact && "mb-1 text-sm leading-[1.6]",
                )}
              >
                {description}
              </p>
            </div>
            {children}
          </div>

          <aside
            className={cn(
              previewPanelClassName,
              compact && "rounded-xl bg-[#14161a] p-3 shadow-none",
            )}
            style={compact ? undefined : { background: previewPanelBackground }}
          >
            {previewContent}
          </aside>
        </section>
      </div>
      <UpgradePlanModal
        description="You don't have enough credits for this generation. Try changing the parameters of your reel, or upgrade to continue."
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Additional credits required"
      />
    </>
  );
}
